import React, { useEffect, useMemo, useState } from 'react';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import useForm from 'hooks/useForm';
import { AppState } from 'reducers/reducers';
import { selectedSpotId } from 'selectors';
import { trackPageViewed } from 'utils/analytics';
import { scrollMainToTop } from 'utils/helpers';
import Button from 'components/Button/Button';
import InlineSelect from 'components/AGForm/Inputs/InlineSelect/InlineSelect';
import { Option } from 'types/SelectInputs';
import TextInput from 'components/AGForm/Inputs/TextInput/TextInput';
import AGForm from 'components/AGForm/AGForm';
import styles from './ValidationGeneratorModuleContainer.module.css';
import { useOrganization } from 'contexts/OrganizationsContext';

type ValidationGeneratorModuleContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const modeOptions = [
    { value: 'minutes', label: 'Hours Mode' },
    { value: 'calendar', label: 'Calendar Mode' },
];

function ValidationGeneratorModuleContainer({ spotId, profile, push }: ValidationGeneratorModuleContainerProps) {
    const { organization } = useOrganization();
    const [validationMode, setValidationMode] = useState<Option>(modeOptions[0]);
    const { handleInput, inputs } = useForm();

    if (!spotId || !organization) return <></>;

    const link = useMemo(() => {
        const minutes = parseInt(inputs.hours || 0) * 60 + parseInt(inputs.minutes || 0);
        return `/organization/${organization.pk}/spots/${spotId}/validation?${
            validationMode.value === 'minutes' ? `&minutes=${minutes || 120}` : '&calendar=true'
        }`;
    }, [inputs, organization, spotId, validationMode]);

    const enableSubmit = useMemo(() => {
        return (
            validationMode.value === 'calendar' ||
            ((!inputs.hours || inputs.hours >= 0) && (!inputs.minutes || inputs.minutes >= 0))
        );
    }, [validationMode, inputs]);

    useEffect(() => {
        scrollMainToTop();
        trackPageViewed({
            pageName: 'Kiosk Generator',
            organizationId: organization?.pk,
            spotId,
            affiliationRole: organization?.role,
            superuser: profile.superuser,
        });
    }, [organization?.pk, organization?.role, spotId, profile.superuser]);

    return (
        <div className={styles.container}>
            <AGForm>
                <InlineSelect
                    options={modeOptions}
                    selected={validationMode}
                    onSelectOption={setValidationMode}
                    disabled={false}
                />

                {validationMode.value === 'minutes' && (
                    <div>
                        <h4>Validate guests for:</h4>
                        <div className={styles.timeInputs}>
                            <TextInput
                                name="hours"
                                label=""
                                type="number"
                                min="0"
                                placeholder="Hours"
                                errors={null}
                                value={inputs.hours}
                                onInput={handleInput}
                            />
                            <TextInput
                                name="minutes"
                                label=""
                                type="number"
                                min="0"
                                placeholder="Minutes"
                                errors={null}
                                value={inputs.minutes}
                                onInput={handleInput}
                            />
                        </div>
                    </div>
                )}
                <Button onClick={() => push(link)} disabled={!enableSubmit}>
                    Start Kiosk
                </Button>
            </AGForm>
        </div>
    );
}

function mapStateToProps(state: AppState) {
    return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        spotId: selectedSpotId(state)!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
    };
}

const mapDispatchToProps = {
    push,
};

export default connect(mapStateToProps, mapDispatchToProps)(ValidationGeneratorModuleContainer);
